import React, { useState, useEffect } from 'react';
import { FaApple, FaAndroid, FaWindows, FaTimes, FaDownload } from 'react-icons/fa';
import api from '../services/api';

const DISMISS_KEY = 'smart_banner_dismissed_v2';

/* ── Platform metadata ─────────────────────────────────────────────────── */
const PLATFORM_CONFIG = {
  ios: {
    icon: FaApple,
    iconClass: 'text-gray-300 text-lg',
    subtitle: 'Open in the App Store',
    btnLabel: 'App Store',
  },
  android: {
    icon: FaAndroid,
    iconClass: 'text-green-400 text-lg',
    subtitle: 'Get it on Google Play',
    btnLabel: 'Play Store',
  },
  windows: {
    icon: FaWindows,
    iconClass: 'text-cyan-400 text-lg',
    subtitle: 'Download for Windows',
    btnLabel: 'Download',
  },
  macos: {
    icon: FaApple,
    iconClass: 'text-gray-300 text-lg',
    subtitle: 'Download for macOS',
    btnLabel: 'Download',
  },
};

/* ── Detect user platform ──────────────────────────────────────────────── */
function detectPlatform() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';

  // Mobile first
  const isIOS =
    /iPhone|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  if (isIOS)           return 'ios';
  if (/Android/i.test(ua)) return 'android';

  // Desktop
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh/i.test(ua)) return 'macos';

  return null;
}

function isSafari() {
  const ua = navigator.userAgent || '';
  return /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgA/i.test(ua);
}

/* ── Check if a download URL is actually configured / ready ────────────── */
function isUrlReady(url) {
  if (!url) return false;
  // Store links are always considered ready
  if (url.startsWith('https://')) return true;
  // For file downloads (/downloads/...), admin must have set one
  if (url.startsWith('/downloads/')) return true;
  return false;
}

export default function SmartAppBanner() {
  const [platform, setPlatform] = useState(null);
  const [visible,  setVisible]  = useState(false);
  const [dlUrls,   setDlUrls]   = useState({});
  const [ready,    setReady]     = useState(false);
  const bannerRef = React.useRef(null);

  // Fetch configured download URLs (same source as Download page)
  useEffect(() => {
    api.get('/api/config/downloads').then(res => {
      if (res.data) {
        const { _versions, _sizes, ...urls } = res.data;
        setDlUrls(urls);
      }
      setReady(true);
    }).catch(() => setReady(true));
  }, []);

  // Keep --banner-h CSS variable in sync with actual rendered height
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const update = () => {
      const h = el.offsetHeight;
      document.documentElement.style.setProperty('--banner-h', h + 'px');
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visible]);

  // Reset CSS var when banner hides
  useEffect(() => {
    if (!visible) {
      document.documentElement.style.setProperty('--banner-h', '0px');
    }
  }, [visible]);

  // Show banner once config is loaded and platform is detected
  useEffect(() => {
    if (!ready) return;

    const pl = detectPlatform();
    if (!pl) return;

    // iOS Safari: native banner from meta tag handles it
    if (pl === 'ios' && isSafari()) return;

    // Check if download is configured for this platform
    if (!isUrlReady(dlUrls[pl])) return;

    // Check dismissal (per-platform)
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        const dismissed = data[pl];
        if (dismissed) {
          const thirtyDays = 30 * 24 * 60 * 60 * 1000;
          if (Date.now() - dismissed < thirtyDays) return;
        }
      }
    } catch (_) {}

    setPlatform(pl);
    setVisible(true);
  }, [ready, dlUrls]);

  const dismiss = () => {
    setVisible(false);
    try {
      const existing = JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}');
      existing[platform] = Date.now();
      localStorage.setItem(DISMISS_KEY, JSON.stringify(existing));
    } catch (_) {}
  };

  const openLink = () => {
    const url = dlUrls[platform];
    if (!url) return;
    if (url.startsWith('https://')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      // Direct file download
      window.location.href = url;
    }
    dismiss();
  };

  if (!visible || !platform) return null;

  const cfg = PLATFORM_CONFIG[platform];
  if (!cfg) return null;
  const IconComponent = cfg.icon;

  return (
    <div
      ref={bannerRef}
      className="w-full flex items-center gap-3 px-3 py-2
                 bg-gray-900 border-b border-gray-700 shadow-lg"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
    >
      {/* App icon */}
      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500
                      flex items-center justify-center text-white shadow-md">
        <span className="text-xs font-black leading-none">XO</span>
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-semibold truncate leading-tight">VPN XO</p>
        <p className="text-gray-400 text-xs truncate leading-tight">{cfg.subtitle}</p>
      </div>

      {/* Platform icon */}
      <IconComponent className={`${cfg.iconClass} flex-shrink-0`} />

      {/* CTA button */}
      <button
        onClick={openLink}
        className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold
                   bg-gradient-to-r from-blue-600 to-cyan-500 text-white
                   hover:from-blue-500 hover:to-cyan-400 transition-all shadow-sm"
      >
        {cfg.btnLabel}
      </button>

      {/* Dismiss */}
      <button
        onClick={dismiss}
        className="flex-shrink-0 p-1 text-gray-500 hover:text-gray-300 transition-colors"
        aria-label="Dismiss"
      >
        <FaTimes size={12} />
      </button>
    </div>
  );
}
